/** Browser mirror of src/shared/plainContent.ts. webMirrorContracts pins the body. */

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

const isCsiFinal = (code: number): boolean => code >= 0x40 && code <= 0x7e;

/** Decode raw model/shell content at web and terminal paint boundaries. Journal bytes remain intact.
 * Control sequences are not content. Terminal paint expands tabs; authored Markdown keeps source tabs. */
export function plainContent(input: string, tabs: 'expand' | 'preserve' = 'expand'): string {
  const lines = [''];
  const current = (): string => lines[lines.length - 1]!;
  const setCurrent = (value: string): void => { lines[lines.length - 1] = value; };

  for (let i = 0; i < input.length;) {
    const code = input.charCodeAt(i);
    if (code === 0x1b) {
      const kind = input[i + 1];
      if (kind === '[') {
        i += 2;
        while (i < input.length && !isCsiFinal(input.charCodeAt(i))) i++;
        if (i < input.length) i++;
        continue;
      }
      if (kind === ']' || kind === 'P' || kind === '^' || kind === '_') {
        i += 2;
        while (i < input.length) {
          if (input.charCodeAt(i) === 0x07) { i++; break; }
          if (input.charCodeAt(i) === 0x1b && input[i + 1] === '\\') { i += 2; break; }
          i++;
        }
        continue;
      }
      i += Math.min(2, input.length - i);
      continue;
    }
    if (code === 0x9b) {
      i++;
      while (i < input.length && !isCsiFinal(input.charCodeAt(i))) i++;
      if (i < input.length) i++;
      continue;
    }
    if (code === 0x0a) { lines.push(''); i++; continue; }
    if (code === 0x0d) {
      if (input.charCodeAt(i + 1) === 0x0a) i++;
      lines.push('');
      i++;
      continue;
    }
    if (code === 0x09) {
      // Expanding an authored code tab would also corrupt the code copied from its rendered DOM.
      const tab = tabs === 'preserve' ? '\t' : ' '.repeat(8 - ([...graphemeSegmenter.segment(current())].length % 8));
      setCurrent(current() + tab);
      i++;
      continue;
    }
    if (code === 0x08) {
      const graphemes = [...graphemeSegmenter.segment(current())].map(({ segment }) => segment);
      graphemes.pop();
      setCurrent(graphemes.join(''));
      i++;
      continue;
    }
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) { i++; continue; }
    const point = input.codePointAt(i)!;
    setCurrent(current() + String.fromCodePoint(point));
    i += point > 0xffff ? 2 : 1;
  }
  return lines.join('\n');
}
